import { useTheme } from "@mui/material/styles";
import SvgIcon, { type SvgIconProps } from "@mui/material/SvgIcon";
import { forwardRef, useId } from "react";

type ScoutSidePanelIconProps = SvgIconProps & {
  /** When true, the right panels widen and the content pane shrinks. */
  open?: boolean;
};

/**
 * Contiguous view-sidebar outline (Material ViewSidebarOutlined geometry):
 * one outer frame with holes for the content pane and three panel cells.
 * When `open`, the content hole shrinks and the panel holes widen.
 */
const ScoutSidePanelIcon = forwardRef<SVGSVGElement, ScoutSidePanelIconProps>(
  function ScoutSidePanelIcon({ open = false, ...props }, ref) {
    const theme = useTheme();
    const reactId = useId().replace(/:/g, "");
    const maskId = `scout-side-panel-mask-${reactId}`;

    const transition = theme.transitions.create("transform", {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    });

    return (
      <SvgIcon ref={ref} viewBox="0 0 24 24" {...props}>
        <mask id={maskId}>
          {/* White = keep frame; black = holes. Inline fill so MUI's
              currentColor on the SvgIcon root cannot wash out the mask. */}
          <rect width="24" height="24" style={{ fill: "#fff" }} />
          {/* Content hole — shrinks from the left (right edge moves inward). */}
          <g
            style={{
              transformBox: "view-box",
              transformOrigin: "4px 12px",
              transform: open ? "scaleX(0.7)" : "scaleX(1)",
              transition,
            }}
          >
            <rect x="4" y="6" width="11.5" height="12" style={{ fill: "#000" }} />
          </g>
          {/* Panel holes — widen from the right into the content area. */}
          <g
            style={{
              transformBox: "view-box",
              transformOrigin: "20px 12px",
              transform: open ? "scaleX(2.2)" : "scaleX(1)",
              transition,
            }}
          >
            <rect x="17.5" y="6" width="2.5" height="2.67" style={{ fill: "#000" }} />
            <rect x="17.5" y="10.67" width="2.5" height="2.67" style={{ fill: "#000" }} />
            <rect x="17.5" y="15.33" width="2.5" height="2.67" style={{ fill: "#000" }} />
          </g>
        </mask>
        {/* Same outer bounds as ViewSidebarOutlined. */}
        <rect x="2" y="4" width="20" height="16" fill="currentColor" mask={`url(#${maskId})`} />
      </SvgIcon>
    );
  }
);

export default ScoutSidePanelIcon;
